// SettingsRow — the labelled preference row, pure.
//
// The workhorse of the settings surface: one caption (+ optional description)
// paired with one control, so every preference reads consistently. It is the
// Field pattern turned horizontal — caption on the left, control on the right,
// grouped by whitespace, wrapping to a stack when the row gets narrow.
//
// Association (a11y): when the control is a single native/id-bearing element,
// pass `htmlFor` and the row renders a real <label> bound to it. Many design
// controls (Switch, Select, SegmentedControl) instead take their own
// accessible name via an `ariaLabel`/`label` prop — for those, leave `htmlFor`
// unset and pass the SAME caption text into the control's name prop; the row
// then renders the caption as a plain <span> (the Switch-story precedent), so
// the visible caption and the control's name agree without double-labelling.
//
// Register (.claude/research/design/06): no resting border, no per-row divider — rows are
// grouped by whitespace inside their section (§2). The description sits on the
// dim ink ramp; nothing spends the accent (a preference caption is not "the
// primary action in this view").
//
// Pure/props-driven: label, description and the control all arrive as props;
// the row owns no state and performs no I/O. `control` is a slot the consumer
// fills with whatever primitive the preference needs — the row never knows the
// control's shape. All user-facing text is caller-owned (localised), so no
// Lingui macro lives here.

import type { ReactNode } from "react";
import { Text } from "../../Text/Text.js";
import "./SettingsRow.css";

export type SettingsRowProps = {
    // The visible caption for the preference.
    readonly label: ReactNode;
    // Optional one-line explanation under the caption (dim ink).
    readonly description?: ReactNode;
    // When the control is a single id-bearing element, its id — the caption
    // becomes a real <label htmlFor>. Omit for group controls that name
    // themselves (Switch/Select/SegmentedControl via their own name prop).
    readonly htmlFor?: string;
    // The control slot: Switch, Select, SegmentedControl, Input, Button…
    readonly control: ReactNode;
};

export const SettingsRow = ({
    label,
    description,
    htmlFor,
    control,
}: SettingsRowProps) => {
    const caption =
        htmlFor != null ? (
            <label htmlFor={htmlFor} className="bilda-settings-row__label">
                {label}
            </label>
        ) : (
            <span className="bilda-settings-row__label">{label}</span>
        );

    return (
        <div className="bilda-settings-row">
            <div className="bilda-settings-row__text">
                {caption}
                {description != null ? (
                    <Text tone="dim" size="xs">
                        {description}
                    </Text>
                ) : null}
            </div>
            <div className="bilda-settings-row__control">{control}</div>
        </div>
    );
};
